/**
 * 缓存资源工具函数
 * 提供便捷的方法来加载和使用缓存的图片和视频资源
 */

import { getCachedResourceBlob } from '@/lib/resource-cache'

/**
 * 判断是否为图片 URL
 */
const isImageUrl = (url: string): boolean => {
  if (!url) return false
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
  const lowerUrl = url.toLowerCase()
  return imageExtensions.some(ext => lowerUrl.includes(ext))
    || /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(url)
}

/**
 * 判断是否为视频 URL
 */
const isVideoUrl = (url: string): boolean => {
  if (!url) return false
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.flv']
  const lowerUrl = url.toLowerCase()
  return videoExtensions.some(ext => lowerUrl.includes(ext))
    || /\.(mp4|webm|ogg|mov|avi|flv)(\?|$)/i.test(url)
}

/**
 * 判断是否为需要缓存的资源 URL
 */
export const isCacheableResource = (url: string): boolean => {
  if (!url) return false
  // 排除 data URL 和 blob URL
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return false
  }
  // 只缓存图片和视频
  return isImageUrl(url) || isVideoUrl(url)
}

/**
 * 获取缓存的资源 URL（自动判断类型）
 * 返回 Blob URL，由调用方负责管理生命周期（使用 URL.revokeObjectURL 释放）
 */
export const getCachedResourceUrl = async (url: string): Promise<string> => {
  if (!isCacheableResource(url)) {
    return url
  }

  try {
    const blob = await getCachedResourceBlob(url)
    if (blob) {
      return URL.createObjectURL(blob)
    }
  } catch (error) {
    console.warn('获取缓存资源失败，使用原始 URL:', error)
    throw error
  }

  return url
}

/**
 * 预加载资源到缓存（仅缓存，不创建 Blob URL）
 */
export const preloadResource = async (url: string): Promise<void> => {
  if (!isCacheableResource(url)) {
    return
  }

  try {
    // 只需要加载到缓存，不需要创建 Blob URL
    await getCachedResourceBlob(url)
  } catch (error) {
    console.warn('预加载资源失败:', error)
  }
}

/**
 * 批量预加载资源
 */
export const preloadResources = async (urls: string[]): Promise<void> => {
  const promises = urls.map(url => preloadResource(url))
  await Promise.allSettled(promises)
}
